import i18n from "i18next";
import { initReactI18next } from "react-i18next";
import { nl } from "./locales/nl";
import { en } from "./locales/en";
import { de } from "./locales/de";
import type { Language } from "@/lib/auth";

// Dutch, German or English (docs/design_handoff_partner/README.md §3). Before signing in: what was chosen
// on this browser, else the browser's own language. Signed in: the account's language, which the mails
// use too; the user menu changes both.

export const LANGUAGES: readonly Language[] = ["nl", "de", "en"];
/** Each language in its own words: whoever cannot read the current one still finds theirs. */
export const LANGUAGE_NAMES: Record<Language, string> = { nl: "Nederlands", de: "Deutsch", en: "English" };

const KEY = "wlx:partner:locale";

export function asLanguage(value: string | null | undefined): Language | null {
  return value === "nl" || value === "en" || value === "de" ? value : null;
}

function initialLanguage(): Language {
  try {
    const stored = asLanguage(localStorage.getItem(KEY));
    if (stored) return stored;
  } catch {
    // no storage: the browser's language
  }
  const browser = typeof navigator !== "undefined" ? navigator.language.slice(0, 2).toLowerCase() : "nl";
  return asLanguage(browser) ?? "nl";
}

/** Whether someone chose a language on this browser (then an invitation in another language does not overrule it). */
export function languageChosen(): boolean {
  try {
    return asLanguage(localStorage.getItem(KEY)) !== null;
  } catch {
    return false;
  }
}

void i18n.use(initReactI18next).init({
  resources: { nl: { translation: nl }, en: { translation: en }, de: { translation: de } },
  lng: initialLanguage(),
  fallbackLng: "nl",
  keySeparator: false,
  interpolation: { escapeValue: false },
});
// The page says which language it is in (screen readers, hyphenation), and the tab its name.
function followLanguage(language: string) {
  document.documentElement.lang = language;
  document.title = `${i18n.t("portal.name")} · Willems Logistics`;
}
followLanguage(i18n.language);
i18n.on("languageChanged", followLanguage);

/** Shows the portal in this language; `remember` keeps it for this browser. */
export function showLanguage(language: Language, remember = true): void {
  if (remember) {
    try {
      localStorage.setItem(KEY, language);
    } catch {
      // a preference only
    }
  }
  if (i18n.language !== language) void i18n.changeLanguage(language);
}

export function currentLanguage(): Language {
  return asLanguage(i18n.language) ?? "nl";
}

export default i18n;
